import { MaintenanceIntervalType, MaintenanceTemplate } from '../../types/Enums';
import type { ComponentMaintenanceData, MaintenanceActivity } from '../../Helpers/maintenanceCostUtils';
import type { UpcomingMaintenanceItem } from './useUpcomingMaintenance';

/** An active plan's activity as stored, with the board's snooze fields */
type StoredActivity = Partial<MaintenanceActivity> & Pick<UpcomingMaintenanceItem, 'snoozedUntil' | 'snoozedBy' | 'snoozedAt'>;

/** One building's `maintenance/active` node, as far as the board reads it */
export interface StoredActivePlan {
    components?: Record<string, Partial<Omit<ComponentMaintenanceData, 'maintenanceActivities'>> & {
        maintenanceActivities?: Record<string, StoredActivity>;
    }>;
}

/** A component node, as far as the board reads it */
export interface StoredComponent {
    specificType?: { value?: string };
    label?: { value?: string };
}

/**
 * The board's maintenance items for one building's active plan.
 *
 * Follows the plan page (`resolveComponentSchedule`): an activity counts only
 * while its component exists with a type, it is neither disabled nor removed,
 * and its template still exists. The name comes from the template, never the key.
 */
export const upcomingMaintenanceItems = (
    buildingKey: string,
    buildingName: string,
    plan: StoredActivePlan | null | undefined,
    components: Record<string, StoredComponent | undefined>,
    findTemplate: (templateId: string) => MaintenanceTemplate | null
): UpcomingMaintenanceItem[] => {
    const items: UpcomingMaintenanceItem[] = [];

    Object.entries(plan?.components ?? {}).forEach(([componentKey, componentData]) => {
        if (!componentData?.maintenanceActivities) return;
        if (componentData.excluded) return;

        const component = components[componentKey];
        const componentType = component?.specificType?.value;
        if (!componentType) return; // component deleted, or without a type
        // Components use label.value for custom names, otherwise use type translation
        const componentLabel = component?.label?.value || undefined;

        Object.entries(componentData.maintenanceActivities).forEach(([templateId, activity]) => {
            if (!activity?.nextDate) return;
            if (activity.disabled) return;
            if (componentData.removedActivities?.[templateId]) return;

            const template = findTemplate(templateId);
            if (!template) return; // template deleted since the plan was activated

            // Validate the next date format (YYYY-MM-DD). How far in
            // advance to show activities is governed by the look-ahead
            // window in ManagementPage, so no year cap is applied here —
            // all upcoming and overdue activities are returned.
            if (activity.nextDate.split('-').length !== 3) return;

            items.push({
                key: `${buildingKey}_${componentKey}_${templateId}`,
                buildingKey,
                buildingName,
                componentKey,
                componentLabel,
                componentType,
                templateId,
                activityName: template.name,
                nextDate: activity.nextDate,
                intervalType: activity.intervalType || MaintenanceIntervalType.years,
                intervalValue: activity.intervalValue || 1,
                cost: activity.cost,
                comment: typeof activity.comment === 'string' && activity.comment ? activity.comment : undefined,
                // Snooze fields
                snoozedUntil: activity.snoozedUntil,
                snoozedBy: activity.snoozedBy,
                snoozedAt: activity.snoozedAt
            });
        });
    });

    return items;
};
